import React, { useState, useEffect } from 'react';
import { ActionIcon } from '@mantine/core';
import { spotlight } from '@mantine/spotlight';
import { Search } from 'tabler-icons-react';
import { Link } from '@inertiajs/react';
import { useUser } from '../../src/context/UserContext';

// Component imports
import SpotlightSearch from './SpotlightSearch';
import SearchModal from './SearchModal';
import UserSection from './UserSection';

// Hook import
import { useSearch } from './hooks/useSearch';

// Asset imports
import logoUrl from '/assets/NEW WCWD LOGO WHITE.svg';
import mobileLogoUrl from '/assets/Logomark_orange.svg';
import hamburgerIcon from '/assets/white-hamburger-menu.png';

// Styles
import '../../styles/Navbar.css';
import '@mantine/spotlight/styles.css';

interface NavbarProps {
  toggleSidebar: () => void;
  isLoginPage?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar }) => {
  const { user, logout } = useUser();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Search functionality via custom hook
  const {
    searchQuery,
    spotlightActions,
    isSearching,
    showFullResults,
    fullSearchResults,
    fullSearchLoading,
    handleSearchChange,
    handleSpotlightClose,
    handleSpotlightOpen,
    handleBackToSearch,
    closeFullResults,
  } = useSearch();

  const handleSearchIconClick = () => {
    spotlight.open();
  };

  return (
    <div>
      {/* Spotlight Search */}
      <SpotlightSearch
        actions={spotlightActions}
        onSearchChange={handleSearchChange}
        onClose={handleSpotlightClose}
        onOpen={handleSpotlightOpen}
        isSearching={isSearching}
        limit={searchQuery.trim() ? 12 : 30}
      />

      {/* Full Search Results Modal */}
      <SearchModal
        opened={showFullResults}
        onClose={closeFullResults}
        searchQuery={searchQuery}
        fullSearchResults={fullSearchResults}
        isLoading={fullSearchLoading}
        onBackToSearch={handleBackToSearch}
      />

      {/* Main Navigation */}
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="navbar-content">
          {/* Hamburger Menu */}
          <div className="hamburger-icon">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              className="hamburger-button"
              aria-label="Toggle menu"
            >
              <img src={hamburgerIcon} alt="Menu" />
            </button>
          </div>

          {/* Desktop Logo */}
          <div className="desktop-logo">
            <Link href="/">
              <img src={logoUrl} alt="WhereCanWeDance home" className="site-nav-logo" />
            </Link>
          </div>

          {/* Mobile Logo */}
          <div className="mobile-logo">
            <Link href="/" className="mobile-logo-link">
              <img src={mobileLogoUrl} alt="WhereCanWeDance home" className="site-nav-logo" style={{ width: '45px' }} />
            </Link>
          </div>

          {/* Search Icon */}
          <div className="search-icon">
            <ActionIcon
              size="xl"
              color="gray"
              variant="light"
              onClick={handleSearchIconClick}
              className="search-trigger"
              title="Search (Ctrl+K)"
              aria-label="Open search"
            >
              <Search strokeWidth={1.75} color="gray" style={{ width: '24px', height: '24px' }} />
            </ActionIcon>
          </div>

          {/* User Section */}
          <div className="navbar-actions">
            <UserSection user={user} onLogout={logout} />
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
